import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { errorMessage } from '../members/hooks';
import { itemsOf, queryString, useApiQuery } from '../qrs/hooks';
import { formatNumber } from '../qrs/states';
import { stateChipFor, type OperatorQrItem } from '../operator/types';
import { QrTimeline } from './QrTimeline';
import { ScanWeek } from './ScanWeek';
import type { ScanWeekData } from './types';

/** Auditoría de un código para el operador (R6.1, R6.2, R6.6): línea de tiempo, ficha y escaneos de la semana. */
export function QrAuditPage() {
  const { code = '' } = useParams();
  const slot = useApiQuery(
    (signal) =>
      api
        .get<{ items: OperatorQrItem[] }>(`/v1/operator/qrs${queryString({ q: code, limit: 1 })}`, { signal })
        .then((res) => itemsOf(res).find((r) => r.code === code) ?? null),
    [code],
  );
  // Endpoints del operador (R6.7): los del partner exigen `X-Org-Id` y por eso respondían 400 (BUG-25).
  const scans = useApiQuery(
    (signal) => api.get<ScanWeekData>(`/v1/operator/qrs/${encodeURIComponent(code)}/audit/scans?days=7`, { signal }),
    [code],
  );
  const qr = slot.data ?? null;
  const state = stateChipFor(qr?.state ?? null);

  return (
    <section className="space-y-5">
      <Link to="/operador/qr" className="text-sm text-green underline hover:text-ink">
        ← QR de todas las organizaciones
      </Link>
      <div>
        <h1 className="font-display text-xl font-bold text-ink">
          Auditoría · <span className="font-mono tracking-wider">{code}</span>
        </h1>
        <p className="text-sm text-ink-muted">
          Todo lo que le pasó al código desde que existe: dueños, destinos, versiones, escaneos y acciones del operador. Registro de solo lectura, no se
          puede editar ni borrar.
        </p>
      </div>

      {slot.error ? (
        <Banner
          tone="warning"
          action={
            <Button size="sm" variant="secondary" onClick={slot.reload}>
              Reintentar
            </Button>
          }
        >
          No pudimos cargar la ficha del código: {errorMessage(slot.error)}. La línea de tiempo igual se muestra.
        </Banner>
      ) : null}

      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4 xl:grid-cols-[minmax(0,1fr),minmax(18rem,24rem)]">
        <QrTimeline code={code} source="operator" />

        <div className="space-y-4">
          <div className="space-y-2 rounded-lg border border-line bg-white p-4">
            <h2 className="font-display text-base font-bold text-ink">Ficha del código</h2>
            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-ink-muted">Dueño actual</dt>
              <dd>{qr?.orgName ?? qr?.orgId ?? <span className="text-ink-muted">Ninguno</span>}</dd>
              <dt className="text-ink-muted">Estado</dt>
              <dd>
                <Chip tone={state.tone}>{state.label}</Chip>
              </dd>
              <dt className="text-ink-muted">Destino</dt>
              <dd className="break-all">{qr?.targetUrl ?? <span className="text-ink-muted">Sin destino</span>}</dd>
              <dt className="text-ink-muted">Escaneos</dt>
              <dd className="tabular-nums">{scans.error ? '—' : formatNumber(scans.data?.total)}</dd>
            </dl>
          </div>

          <div className="space-y-2 rounded-lg border border-line bg-white p-4">
            <h2 className="font-display text-base font-bold text-ink">Escaneos de la última semana</h2>
            <ScanWeek data={scans.data ?? null} loading={scans.loading} error={scans.error ? errorMessage(scans.error) : null} onRetry={scans.reload} />
          </div>
        </div>
      </div>
    </section>
  );
}
